import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, User, Calendar, Hash, CheckCircle, Clock, Users, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useJotFormSubmission } from "@/hooks/useJotFormSubmissions";
import { useComparativaData } from "./hooks/useComparativaData";
import { SubmissionDetailSkeleton } from "./components/SubmissionDetailSkeleton";
import { AnswersDisplayV2 } from "./components/AnswersDisplayV2";
import { ReferralsSection } from "./components/ReferralsSection";
import { ComparativaIndividualEquipo } from "./components/ComparativaIndividualEquipo";
import { ComparativaPorRol } from "./components/ComparativaPorRol";
import { toast } from "sonner";
import { extractNameFromAnswers } from "@/utils/submissionHelpers";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ReferralSelector } from "../templates_v2/components/ReferralSelector";
import { useProject } from "@/hooks/useProject";
import { useAvailableTemplates } from "./hooks/useAvailableTemplates";
import { TemplateSelector } from "./components/TemplateSelector";
import { validateTemplateFile } from "./hooks/useTemplateValidation";
import { ReportActions, ReportStatusBadge, getReportStatus } from "@/components/reports";
import { useSubmissionReportStatus } from "@/hooks/useReportQueue";

const SubmissionDetailV2 = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const formId = searchParams.get('formId');

  // Mover todos los hooks arriba, antes de cualquier return condicional
  const { projectId } = useProject();
  const { data: submission, isLoading } = useJotFormSubmission(id || '', formId || '');
  const { data: comparativaData, isLoading: isLoadingComparativa } = useComparativaData(submission?.db_id || 0);
  const [selectedReferralIds, setSelectedReferralIds] = useState<number[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  // Extraer email del submission para agrupar referidos
  const getInviterEmail = () => {
    if (!submission?.answers) return null;
    const emailAnswer = Object.values(submission.answers).find((answer: any) => {
      const name = answer?.name?.toLowerCase() || '';
      const text = answer?.text?.toLowerCase() || '';
      return name === 'email' || name === 'correo' || name === 'referido-email' ||
             name.includes('correoelectronico') || name.includes('correo-electronico') ||
             text.includes('correo electrónico');
    });
    const email = (emailAnswer as any)?.answer;
    return email && email !== 'N/A' ? email : null;
  };
  const inviterEmail = getInviterEmail();

  const { data: referralsCount = 0 } = useQuery({
    queryKey: ['referrals-count', submission?.db_id, inviterEmail],
    queryFn: async () => {
      if (!submission?.db_id) return 0;

      // Si tenemos email, buscar referidos de todos los submissions del email
      if (inviterEmail) {
        // Paso 1: Obtener todos los submission IDs del invitador
        const { data: inviterSubmissions, error: inviterError } = await supabase
          .from('jotform_submissions')
          .select('id')
          .eq('email', inviterEmail)
          .eq('is_deleted_in_jotform', false);

        if (inviterError || !inviterSubmissions?.length) {
          return 0;
        }

        const inviterIds = inviterSubmissions.map(s => s.id);

        // Paso 2: Contar referidos de todos esos submissions
        const { count, error } = await supabase
          .from('jotform_submissions')
          .select('*', { count: 'exact', head: true })
          .in('parent_submission_id', inviterIds)
          .eq('is_deleted_in_jotform', false);

        if (error) {
          console.error('Error fetching referrals count:', error);
          return 0;
        }
        return count || 0;
      }

      // Fallback: buscar solo por submission ID
      const { count, error } = await supabase
        .from('jotform_submissions')
        .select('*', { count: 'exact', head: true })
        .eq('parent_submission_id', submission.db_id)
        .eq('is_deleted_in_jotform', false);
      if (error) {
        console.error('Error fetching referrals count:', error);
        return 0;
      }
      return count || 0;
    },
    enabled: !!submission?.db_id && !submission?.is_referral,
  });

  // Obtener jotform_form_id desde el submission (es el form_id en la estructura V2)
  const jotformFormId = submission?.form_id || null;

  // Obtener plantillas disponibles usando el jotform_form_id
  // Filtrar plantillas de referidos si la submission no tiene referidos
  const { templates, isLoading: isLoadingTemplates, hasMultipleTemplates } = useAvailableTemplates({
    formMappingId: null,
    jotformFormId: jotformFormId || null,
    enabled: !!jotformFormId,
    hasReferrals: referralsCount > 0,
  });

  // Si solo hay una plantilla, usarla automáticamente
  useEffect(() => {
    if (templates.length === 1 && !selectedTemplateId) {
      setSelectedTemplateId(templates[0].id);
    } else if (templates.length > 1 && !selectedTemplateId) {
      // Si hay múltiples, usar la primera
      setSelectedTemplateId(templates[0].id);
    }
  }, [templates, selectedTemplateId]);

  // Report status from new queue system
  const currentTemplateId = selectedTemplateId || templates[0]?.id || 0;
  const {
    hasReport: hasReportFromQueue,
    isGenerating,
    hasFailed,
  } = useSubmissionReportStatus(submission?.db_id || 0, currentTemplateId);

  // Use queue-based status if available, fallback to legacy report_url
  const hasReport = hasReportFromQueue || !!submission?.report_url;
  const reportStatusText = isGenerating
    ? "Generando..."
    : hasFailed
    ? "Error"
    : hasReport
    ? "Generado"
    : "Pendiente";

  // Ahora los returns condicionales
  if (!formId) {
    return (
      <div className="p-6">
        <div className="text-center py-8 text-red-600">
          Error: Se requiere el parámetro formId
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <SubmissionDetailSkeleton />;
  }

  if (!submission) {
    return (
      <div className="p-6">
        <div className="text-center py-8 text-red-600">
          Submission no encontrada
        </div>
      </div>
    );
  }

  // Helper to get name from answers
  const getName = () => {
    return extractNameFromAnswers(submission.answers);
  };

  // Helper to get email from answers
  const getEmail = () => {
    if (!submission.answers) return 'N/A';

    const emailAnswer = Object.values(submission.answers).find((answer: any) => {
      const name = answer?.name?.toLowerCase() || '';
      const text = answer?.text?.toLowerCase() || '';
      return name === 'email' || name === 'correo' || name === 'referido-email' ||
             name.includes('correoelectronico') || name.includes('correo-electronico') ||
             text.includes('correo electrónico');
    });

    return (emailAnswer as any)?.answer || 'N/A';
  };

  // Helper to get all codes from answers (including Código Líder if exists)
  const getCodes = () => {
    if (!submission.answers) return {};

    const codes: Record<string, string> = {};

    Object.values(submission.answers).forEach((answer: any) => {
      const name = answer?.name?.toLowerCase() || '';
      const text = answer?.text?.toLowerCase() || '';
      const value = answer?.answer || answer?.text || '';

      if (name.includes('lider') || name.includes('líder') || text.includes('líder')) {
        codes.codigoLider = value;
      } else if (name === 'codigo' || name === 'código' || name === 'code') {
        codes.codigoPrincipal = value;
      }
    });

    return codes;
  };

  // Action handlers
  const handleDownloadReport = async () => {
    // Validar que el template seleccionado tenga archivo PPT antes de intentar generar
    const templateToValidate = selectedTemplateId || 1;

    try {
      setIsDownloading(true);
      toast.info("Validando plantilla...");

      const validation = await validateTemplateFile(templateToValidate);
      if (!validation.isValid) {
        toast.error(validation.error || "Error al validar la plantilla", {
          duration: 6000,
        });
        setIsDownloading(false);
        return;
      }
    } catch (validationError: any) {
      toast.error(`Error de validación: ${validationError.message}`);
      setIsDownloading(false);
      return;
    }

    if (referralsCount > 0) {
      try {
        toast.info("Generando reporte de referidos...");
        let referralIds = selectedReferralIds;
        if (referralIds.length === 0) {
          const { data: allReferrals, error: refsError } = await supabase
            .from('jotform_submissions')
            .select('id')
            .eq('parent_submission_id', submission.db_id)
            .eq('is_deleted_in_jotform', false);
          if (refsError) throw refsError;
          referralIds = (allReferrals || []).map(r => r.id);
        }
        const { data, error } = await supabase.functions.invoke('render-report', {
          body: {
            submissionId: submission.db_id,
            templateId: templateToValidate,
            project_id: projectId,
            format: "base64",
            referralIds
          }
        });
        if (error) throw error;
        if (!data || !data.data) throw new Error("No se recibieron datos del reporte renderizado");
        // Convertir base64 a Blob
        const byteCharacters = atob(data.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: data.contentType });
        // Descargar
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        // Usar el nombre del principal para el archivo
        const principalName = getName().replace(/[^a-zA-Z0-9-_]/g, "_");
        link.download = data.fileName || `reporte_referidos_${principalName || submission.id}.pptx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success("Reporte de referidos generado y descargado correctamente");
      } catch (err: any) {
        toast.error(`Error al generar el reporte: ${err.message}`);
      } finally {
        setIsDownloading(false);
      }
      return;
    }
    
    // Si hay reporte generado previamente, descargarlo
    if (submission.report_url) {
      try {
        setIsDownloading(true);
        toast.info('Descargando reporte...');
        const response = await fetch(submission.report_url);
        if (!response.ok) {
          throw new Error(`Error descargando archivo: ${response.status}`);
        }
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const principalName = getName().replace(/[^a-zA-Z0-9-_]/g, "_");
        a.download = `reporte_${principalName || submission.id}_${Date.now()}.pptx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Reporte descargado correctamente');
      } catch (error: any) {
        console.error('Error downloading report:', error);
        toast.error(`Error al descargar el reporte: ${error.message || 'Intente nuevamente'}`);
      } finally {
        setIsDownloading(false);
      }
      return;
    }
    
    // Si no hay reporte disponible, generarlo usando render-report y descargarlo
    try {
      toast.info('Generando reporte...', {
        duration: 5000
      });

      // Usar render-report para generar el reporte (igual que para referidos)
      // templateToValidate ya fue validado al inicio de la función
      const { data, error } = await supabase.functions.invoke('render-report', {
        body: {
          submissionId: submission.db_id,
          templateId: templateToValidate,
          project_id: projectId,
          format: "base64"
        }
      });

      if (error) {
        throw error;
      }

      if (!data || !data.data) {
        throw new Error("No se recibieron datos del reporte renderizado");
      }

      // Convertir base64 a Blob
      const byteCharacters = atob(data.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: data.contentType || 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
      
      // Descargar
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const principalName = getName().replace(/[^a-zA-Z0-9-_]/g, "_");
      link.download = data.fileName || `reporte_${principalName || submission.id}_${templateToValidate}_${Date.now()}.pptx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Reporte generado y descargado correctamente');
    } catch (error: any) {
      console.error('Error generating report:', error);
      toast.error(`Error al generar el reporte: ${error.message || 'Intente nuevamente'}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const prepareComparativaRolData = () => {
    if (!comparativaData) return [];
    return [
      {
        rol: 'Mentor',
        individual: comparativaData.mentor_individual,
        equipo: comparativaData.mentor_equipo,
        organizacion: comparativaData.mentor_organizacion
      },
      {
        rol: 'Manager',
        individual: comparativaData.manager_individual,
        equipo: comparativaData.manager_equipo,
        organizacion: comparativaData.manager_organizacion
      },
      {
        rol: 'Messenger',
        individual: comparativaData.messenger_individual,
        equipo: comparativaData.messenger_equipo,
        organizacion: comparativaData.messenger_organizacion
      }
    ];
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-6 py-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/submissions')}
            className="flex items-center gap-2 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">
                {getName()}
              </h1>
              <p className="text-sm text-muted-foreground">
                Código: {submission.code || 'No asignado'} • ID: {submission.db_id}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={submission.is_referral ? "secondary" : "default"} className="text-sm px-3 py-1">
                {submission.is_referral ? "Referido" : "Principal"}
              </Badge>
              {submission.is_referral && submission.parent?.jotform_submission_id && (
                <Button
                  variant="link"
                  size="sm"
                  className="px-0"
                  onClick={() => navigate(`/admin/submissions/${submission.parent!.jotform_submission_id}?formId=${submission.parent!.form_id || ''}`)}
                >
                  Ver padre
                  <ExternalLink className="h-4 w-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-6 space-y-6">
        {/* Template, Actions, and Quick Info - Horizontal Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Template Selector */}
          {hasMultipleTemplates && (
            <Card className="lg:col-span-3">
              <CardContent className="pt-4 pb-4">
                <TemplateSelector
                  templates={templates}
                  selectedTemplateId={selectedTemplateId}
                  onTemplateChange={setSelectedTemplateId}
                  isLoading={isLoadingTemplates}
                />
              </CardContent>
            </Card>
          )}

          {/* Report Actions */}
          <Card className={hasMultipleTemplates ? "lg:col-span-4" : "lg:col-span-5"}>
            <CardContent className="pt-4 pb-4 h-full flex items-center">
              <ReportActions
                submissionId={submission.db_id}
                templateId={selectedTemplateId || templates[0]?.id || 1}
                submissionName={getName()}
              />
            </CardContent>
          </Card>

          {/* Quick Info */}
          <Card className={hasMultipleTemplates ? "lg:col-span-5" : "lg:col-span-7"}>
            <CardContent className="pt-4 pb-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-primary/10 rounded-md shrink-0">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-muted-foreground">Email</p>
                    <p className="text-xs font-medium truncate">{getEmail()}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-primary/10 rounded-md shrink-0">
                    <Calendar className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground">Fecha</p>
                    <p className="text-xs font-medium">
                      {new Date(submission.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-primary/10 rounded-md shrink-0">
                    <Hash className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground">Código</p>
                    <p className="text-xs font-semibold">{submission.code || 'N/A'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-primary/10 rounded-md shrink-0">
                    <CheckCircle className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground">Estado</p>
                    <Badge variant={
                      isGenerating ? "outline" :
                      hasFailed ? "destructive" :
                      hasReport ? "default" : "secondary"
                    } className="text-[10px] px-1.5 py-0">
                      {reportStatusText}
                    </Badge>
                  </div>
                </div>

                {submission.is_referral && submission.parent?.name && (
                  <div className="flex items-center gap-2 col-span-2">
                    <div className="p-1.5 bg-primary/10 rounded-md shrink-0">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground">Referente</p>
                      <p className="text-xs font-medium">{submission.parent.name}</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Códigos en el Formulario - Oculto */}

        {/* Parent Submission Info */}
        {submission.is_referral && submission.parent && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Información del Padre</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">ID Padre</p>
                  <p className="text-sm font-medium">{submission.parent_submission_id}</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Código Padre</p>
                  <p className="text-sm font-medium">{submission.parent.code || 'N/A'}</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Email Padre</p>
                  <p className="text-sm font-medium truncate">{submission.parent.email || 'N/A'}</p>
                </div>
                <div className="md:col-span-3 flex items-center justify-between p-3 border rounded-lg bg-background">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Nombre</p>
                    <p className="text-sm font-medium">{submission.parent.name || 'N/A'}</p>
                  </div>
                  {submission.parent.jotform_submission_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/admin/submissions/${submission.parent!.jotform_submission_id}?formId=${submission.parent!.form_id || ''}`)}
                    >
                      Ver formulario principal
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Comparativas - Solo para submissions principales */}
        {!submission.is_referral && comparativaData && (
          <div className="space-y-6">
            <ComparativaIndividualEquipo
              mediaIndividual={comparativaData.media_individual}
              mediaEquipo={comparativaData.media_equipo}
              mediaOrganizacion={comparativaData.media_organizacion}
              miembrosEquipo={comparativaData.miembros_equipo}
              totalParticipantesOrganizacion={comparativaData.total_participantes_organizacion}
              diferenciaIndividualEquipo={comparativaData.diferencia_individual_equipo}
              diferenciaIndividualOrganizacion={comparativaData.diferencia_individual_organizacion}
              diferenciaEquipoOrganizacion={comparativaData.diferencia_equipo_organizacion}
            />
            <ComparativaPorRol data={prepareComparativaRolData()} />
          </div>
        )}

        {/* Respuestas del Formulario */}
        {submission.answers && Object.keys(submission.answers).length > 0 ? (
          <AnswersDisplayV2
            answers={submission.answers}
            mappedAnswers={submission.mapped_answers}
          />
        ) : (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              No hay respuestas disponibles
            </CardContent>
          </Card>
        )}

        {/* Sección de Referidos - Solo para submissions principales */}
        {!submission.is_referral && submission.db_id && (
          <>
            {referralsCount > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <label className="font-medium text-sm">Gestionar Referidos</label>
                    <Badge variant="secondary" className="text-xs">
                      {selectedReferralIds.length} de {referralsCount} seleccionados
                    </Badge>
                  </div>
                </div>
                <ReferralSelector
                  mainSubmissionId={submission.db_id}
                  selectedReferralIds={selectedReferralIds}
                  onSelectionChange={setSelectedReferralIds}
                  inviterEmail={inviterEmail || undefined}
                >
                  <Button variant="outline" className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Gestionar Referidos ({selectedReferralIds.length}/{referralsCount})
                  </Button>
                </ReferralSelector>
              </div>
            )}
            <ReferralsSection submissionId={submission.db_id} inviterEmail={inviterEmail || undefined} />
          </>
        )}

        {/* Metadata JotForm */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Metadata Técnica
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">JotForm Submission ID</p>
                <p className="text-sm font-mono break-all">{submission.id}</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Form ID</p>
                <p className="text-sm font-mono break-all">{submission.form_id}</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">IP Address</p>
                <p className="text-sm font-medium">{submission.ip || 'N/A'}</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Estado en JotForm</p>
                <Badge variant={submission.status === 'ACTIVE' ? "default" : "secondary"}>
                  {submission.status}
                </Badge>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Última Actualización</p>
                <p className="text-sm font-medium">
                  {submission.updated_at
                    ? new Date(submission.updated_at).toLocaleString()
                    : 'N/A'}
                </p>
              </div>
              {!submission.is_referral && referralsCount > 0 && (
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Referidos</p>
                  <Badge variant="secondary" className="text-base">
                    {referralsCount} referido{referralsCount !== 1 ? 's' : ''}
                  </Badge>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SubmissionDetailV2;
